import { Box, Paper, Tab, Tabs, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { PopularThemes } from '../../../../components/PopularThemes';
import useSnackbar from '../../../../hooks/useSnackbar';
import { getCuratedUserSegmentsWithTypesAndPage, getUserSegmentCurationPreview } from '../../../../services/api/Segment';
import { HomeNavigationType, HomeType, IHomePreview, IUserSegment, UserSegmentType } from '../../../../services/types';
import PreviewCollection from './Collection';
import { Control } from './Control';
import PreviewDummy from './Dummy';
import PreviewFeaturedCard from './FeaturedCard';
import HomeNavigation from './HomeNavigation';

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    minWidth: 804,
    maxWidth: 1200,
    minHeight: 600,
    padding: theme.spacing(3),
    paddingTop: theme.spacing(4),
    marginBottom: '110px',
  },
  title: {
    fontWeight: 'bold',
    padding: '4px',
  },
}));

interface IProps {}

const Preview = (props: IProps): JSX.Element => {
  const classes = useStyles();
  const snackbar = useSnackbar();

  const [isLoading, setIsLoading] = useState<boolean>();
  const [preview, setPreview] = useState<IHomePreview & { segmentIds: number[] }>();
  const [selectedSegmentIds, setSelectedSegmentIds] = useState<number[]>([]);
  const [allUserId, setAllUserId] = useState<number>(0);
  const [segmentList, setSegmentList] = useState<IUserSegment[]>([]);
  const [homeType, setHomeType] = useState<HomeType>(HomeType.ALL);

  // handler
  const handleChange = (ids: number[]) => {
    setSelectedSegmentIds(ids);
    fetchPreview(ids);
  };

  const handleChangeUserId = (userId: string) => {
    if (userId) {
      fetchUserSegments(userId);
    } else {
      setSelectedSegmentIds([allUserId]);
    }
  };

  // API
  const fetchSegmentList = useCallback(
    async (keyword = '') => {
      try {
        const [fetchPromise] = getCuratedUserSegmentsWithTypesAndPage<IUserSegment>(homeType, undefined, 1, undefined, keyword);

        const { data } = await fetchPromise;

        const normalSegments = data.items.filter(seg => {
          if (seg.type === UserSegmentType.System && seg.name.toLowerCase() === 'all user') {
            setAllUserId(seg.id!);
            return false;
          } else {
            return true;
          }
        });

        setSegmentList(normalSegments);
      } catch (e) {
        snackbar.error(e);
      } finally {
        setIsLoading(false);
      }
    },
    [homeType, snackbar],
  );

  const fetchUserSegments = async (keyword = '') => {
    try {
      setIsLoading(true);
      const result = await getUserSegmentCurationPreview(Number(keyword), homeType);

      if (result && result.status === 200) {
        const preview = result.data as IHomePreview & { segmentIds: number[] };
        setPreview(preview);
        // setSelectedSegmentIds(result.data.segmentIds); // alluser가 포함돼서 오면 빼도 됨
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPreview = async (ids: number[]) => {
    try {
      setIsLoading(true);
      const result = await getUserSegmentCurationPreview(ids, homeType);

      if (result && result.status === 200) {
        const preview = result.data as IHomePreview & { segmentIds: [] };
        setPreview(preview);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  // watch
  useEffect(() => {
    fetchPreview(selectedSegmentIds);
  }, [homeType]);

  useEffect(() => {
    setSelectedSegmentIds(preview?.segmentIds || []);
  }, [preview]);

  // init
  useEffect(() => {
    fetchSegmentList();
  }, [fetchSegmentList]);

  return (
    <div>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        style={{ width: '100%', marginTop: '13px', marginBottom: '17px', paddingLeft: '4px' }}
      >
        <Typography variant="h6" component="h3">
          <Box>
            <span>Segment Preview</span>
          </Box>
        </Typography>
      </Box>
      <Tabs value={homeType} onChange={(_, value) => setHomeType(value)}>
        <Tab label={`Default Home`} value={HomeType.ALL} />
        <Tab label={`Read Home`} value={HomeType.READ} />
        <Tab label={`Listen Home`} value={HomeType.LISTEN} />
      </Tabs>
      {preview && (
        <Paper
          className={classes.paper}
          style={{
            opacity: isLoading ? 0.3 : 1,
          }}
        >
          <Box marginBottom={2}>
            <Box width="100%" overflow="auto" paddingTop={1}>
              <Box display="flex" flexDirection="row" padding="2px 0" width="max-content">
                {preview?.promotions.map((p, idx) => (
                  <PreviewFeaturedCard
                    key={`preview-promotions-item-${idx}-${p.object?.id}`}
                    title={p.object?.title}
                    subTitle={p.subTitle}
                    actionText={p.actionText}
                    label={p.object?.label}
                    type={p.type}
                    object={p.object}
                    curation={p.curation}
                  />
                ))}
              </Box>
            </Box>
          </Box>
          {homeType !== HomeType.LISTEN && preview.navigationSlots && (
            <Box marginTop={4}>
              <HomeNavigation data={preview.navigationSlots} />
            </Box>
          )}
          <Box marginTop={4}>
            <Typography variant="h6" className={classes.title}>
              Stories
            </Typography>
            <Box display="flex" flexDirection="column" padding="2px 0">
              {preview?.fictionSerials.map((fs, i) => {
                switch (fs.type) {
                  case 'collection':
                  case 'storyBasedRecommended':
                    const collection = fs.data;
                    return <PreviewCollection key={`${collection.trackingId}-${i}`} collection={collection} curation={fs.curation} />;

                  case 'themes':
                    return <PopularThemes themes={fs.data} key={`popular-themes`} />;

                  case 'genres':
                    return (
                      <Box paddingTop={2} paddingBottom={2} key={'genres'}>
                        <Typography style={{ paddingBottom: '8px' }}>{fs.title}</Typography>
                        <HomeNavigation data={fs.data.map(value => ({ type: HomeNavigationType.Genre, value }))} key={`popular-genres`} />
                      </Box>
                    );
                  case 'plusCatalog':
                    return <PreviewDummy title="Radish PLUS Unlimited Audiobooks Catalog" key={`plusCatalog`} />;

                  default:
                    return <PreviewDummy title="Unknown" key={`unknown-${i}`} />;
                }
              })}
            </Box>
          </Box>
        </Paper>
      )}
      {segmentList.length > 0 && (
        <Control
          onChangeSelectedIds={handleChange}
          onChangeUserId={handleChangeUserId}
          segmentList={segmentList}
          allUserId={allUserId}
          selectedIds={selectedSegmentIds}
          forceRefresh={() => fetchPreview(selectedSegmentIds)}
        />
      )}
    </div>
  );
};

export default Preview;
